import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { InputTextModule } from 'primeng/inputtext';
import { AutoFocusModule } from 'primeng/autofocus';
import { ToastMessageOptions } from 'primeng/api';
import { InputService, UtilityService } from '@app/core/services';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectFilterEvent, SelectModule } from 'primeng/select';
import { debounceTime, Observable, Subject } from 'rxjs';

export interface InputBlockEmitter {
  property: string
  isSendingData: boolean
  message?: ToastMessageOptions
}

@Component({
  selector: 'app-block-input',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ButtonGroupModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    AutoFocusModule,
  ],
  templateUrl: './block-input.html'
})
export class BlockInput {
  @Input() object!: any;
  @Input() label!: string;
  @Input() property!: string;
  @Input() type!: 'text' | 'number' | 'select' | 'link';
  @Input() class!: 'Product' | 'EbayProduct' | 'EbayStock';
  @Input() suffix: string = '';
  @Input() filter: { [x: string]: string | number } = {};
  @Input() limit!: number;
  @Input('disabled') isDisabled: boolean = false;

  @Output() objectChange = new EventEmitter<any>(); // For two way binding of [(object)]
  @Output() isSendingData = new EventEmitter<InputBlockEmitter>();

  isEditMode: boolean = false;
  isLoadingMode: boolean = false;

  isSendingInputData: boolean = false;

  value: any = null;
  defaultValue: any = this.value;

  selectOptions: { id: number, nom: string }[] = []
  selectSubject = new Subject<string>();

  constructor(
    private inputService: InputService,
    private utilityService: UtilityService
  ) {
    this.selectSubject.pipe(debounceTime(300)).subscribe(value => this.getSelectOptions(value));
  }

  ngOnInit(): void {
    this.value = this.property in this.object ? this.object[this.property] : '';
    this.defaultValue = this.property in this.object ? this.object[this.property] : '';
    if (this.property === 'pays_origine' && this.value) {
      const objectValue = this.property in this.object ? this.object[this.property] : '';
      this.value = {id: objectValue, nom: objectValue}
      this.defaultValue = {id: objectValue, nom: objectValue}
    }
  }

  onToggleMode() {
    this.isEditMode = !this.isEditMode;

    if (this.isEditMode)
      this.getSelectOptions()
  }

  getSelectOptions(value?: string) {
    this.isLoadingMode = true;
    let requestFilter = { ...this.filter }
    if(value) requestFilter = {...requestFilter, nom: value}

    this.utilityService.getSelectOptionsObservable(this.property, requestFilter)
      .subscribe((data) => {
        this.selectOptions = data.results
        if (this.defaultValue && !this.selectOptions.find((option) => option.id === this.defaultValue.id))
          this.selectOptions.unshift(this.defaultValue)
        this.isLoadingMode = false
      })
  }

  onSelectFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.selectSubject.next(value);
  }

  onInputClose() {
    this.value = this.defaultValue;
    this.onToggleMode();
  }

  onSaveValue() {
    this.onToggleMode();

    if (this.defaultValue !== this.value) {
      this.isSendingInputData = true;
      this.isSendingData.emit({ property: this.property, isSendingData: true });

      const data: any = {};
      data[this.property] = this.value?.id ?? this.value;
      data[`${this.property}_id`] = this.value?.id ?? this.value; // If select, send the id only

      let observable: Observable<any>;

      switch (this.class) {
        case 'Product':
          observable = this.inputService.patchProduct(this.object.id, data)
          break;
        case 'EbayProduct':
          observable = this.inputService.patchEbayProduct(this.object.id, data)
          break;
        case 'EbayStock':
          observable = this.inputService.patchEbayStock(this.object.id, data)
          break;
      }

      if (!observable) return;

      observable.subscribe({
        next: (obj: any) => {
          this.object = { ...this.object, ...data };
          this.defaultValue = this.value;
        },
        error: (err: any) => {
          this.isSendingInputData = false;
          this.value = this.defaultValue;
        },
        complete: () => {
          this.isSendingInputData = false;
          this.objectChange.emit(this.object);
          this.isSendingData.emit({ property: this.property, isSendingData: false, message: { severity: 'info', summary: 'Modification', detail: `${this.label} a été changé avec succès` } });
        }
      });
    }
  }
}
